import PropTypes from 'prop-types'
import clsx from 'clsx'

import AvatarTask from './AvatarTask'
import { AUDIENCES, AUDIENCE_ORDER, PRIORITY_LABELS } from './content'
import { AUDIENCE_ICONS } from './icons'
import styles from './style.module.scss'

/*
 * Eine Folie der Schulung.
 *
 * Jede Themenfolie beantwortet dieselben Fragen an derselben Stelle: Für wen
 * ist das (rechts oben), wozu gibt es das (unter dem Titel), was passiert
 * konkret, woran hängt es, und was erspart einem Ärger. Wer zwanzig Folien
 * durchblättert, soll den Blick nicht jedes Mal neu suchen müssen.
 *
 * 16:9 kommt nicht aus einer festen Pixelgröße, sondern aus `aspect-ratio` am
 * Rahmen — die Folie füllt die Breite, die sie bekommt, und die Schrift
 * skaliert über `cqw` mit. Eine feste Bühne mit `transform: scale()` wäre
 * exakter, aber Text darin ist nicht mehr markierbar und Screenreader stolpern.
 */

/*
 * Zielgruppe und Einstufung in **einem** Kasten rechts oben.
 *
 * Sie standen an zwei Stellen: die vier Symbole im Kopf, die drei Balken unten
 * in der Seitenspalte. Beide beantworten dieselbe Frage — „geht mich das etwas
 * an, und wie dringend?" —, und wer sie beantwortet haben will, musste über die
 * ganze Folie blicken. Zusammen sind sie schmaler als vorher jede für sich, und
 * die Seitenspalte bleibt dem Bild.
 */
export function SlideMeta({ audience, priority }) {
	const aktiv = new Set(audience || [])

	return (
		<div className={styles.meta}>
			{/* Immer alle vier, immer in derselben Reihenfolge — nicht Betroffene
			    werden blass statt ausgeblendet, sonst verschöbe sich die Reihe von
			    Folie zu Folie und die feste Stelle wäre dahin. */}
			<div className={styles.audience} aria-label="Zielgruppe">
				{AUDIENCE_ORDER.map(key => {
					const Icon = AUDIENCE_ICONS[key]
					const an = aktiv.has(key)

					return (
						<span
							key={key}
							className={clsx(styles.audienceItem, an && styles.audienceOn)}
							title={`${AUDIENCES[key].label}: ${an ? 'betroffen' : 'nicht betroffen'}`}
						>
							<Icon className={styles.audienceIcon} />
							<span className={styles.audienceLabel}>{AUDIENCES[key].short}</span>
						</span>
					)
				})}
			</div>

			{/* Relevanz, Dringlichkeit, Wichtigkeit: keine Zahlen und keine
			    Prozente. Drei gefüllte Kästchen sagen „viel", ohne eine
			    Genauigkeit zu behaupten, die dahinter nicht steckt. */}
			{priority && (
				<div className={styles.priority}>
					{Object.keys(PRIORITY_LABELS).map(key => (
						<div key={key} className={styles.priorityRow}>
							<span className={styles.priorityLabel}>{PRIORITY_LABELS[key]}</span>
							<span
								className={styles.priorityDots}
								aria-label={`${PRIORITY_LABELS[key]}: ${priority[key]} von 3`}
							>
								{[1, 2, 3].map(stufe => (
									<i
										key={stufe}
										className={clsx(styles.dot, stufe <= priority[key] && styles.dotOn)}
									/>
								))}
							</span>
						</div>
					))}
				</div>
			)}
		</div>
	)
}

SlideMeta.propTypes = {
	audience: PropTypes.arrayOf(PropTypes.string),
	priority: PropTypes.object
}

/**
 * Die Checkliste der Schüler:innen, nachgebaut.
 *
 * Sie steht anstelle der „Woran es hängt"-Spalte auf der Folie zu den
 * Voraussetzungen. Der Grund: Die Aufzählung der Schritte beschrieb etwas, das
 * SuS, LuL und Verwaltung ohnehin täglich vor sich haben — die Liste selbst
 * sagt in einem Blick, dass alle drei dasselbe sehen.
 */
export function ChecklistCard({ checklist }) {
	const { done = 0, total = 0 } = checklist
	const anteil = total ? Math.round((done / total) * 100) : 0

	return (
		<div className={styles.check}>
			<div className={styles.checkHead}>
				<span className={styles.checkTitle}>{checklist.title}</span>
				<span className={styles.checkCount}>
					{done} von {total}
				</span>
			</div>

			<span className={styles.checkBar}>
				<i className={styles.checkBarFill} style={{ width: `${anteil}%` }} />
			</span>

			<ul className={styles.checkList}>
				{checklist.items.map(item => (
					<li key={item.text} className={clsx(item.done && styles.checkDone, item.mark && styles.checkMark)}>
						<span className={clsx(styles.checkBox, item.done && styles.checkBoxOn)}>
							{item.done ? '✓' : ''}
						</span>
						<span dangerouslySetInnerHTML={{ __html: markup(item.text) }} />
					</li>
				))}
			</ul>

			{checklist.note && <p className={styles.checkNote}>{checklist.note}</p>}
		</div>
	)
}

ChecklistCard.propTypes = { checklist: PropTypes.object.isRequired }

/** Ein Verweis nach außen — anklickbar statt abgetippt. */
export function LinkCard({ link }) {
	return (
		<div className={styles.linkCard}>
			<p className={styles.linkLabel}>{link.label}</p>
			<a className={styles.linkTitle} href={link.url} target="_blank" rel="noreferrer">
				{link.title}
			</a>
			<p className={styles.linkSource}>{link.source}</p>
			<p className={styles.linkUrl}>{link.short}</p>
			{link.note && <p className={styles.linkNote}>{link.note}</p>}
		</div>
	)
}

LinkCard.propTypes = { link: PropTypes.object.isRequired }

/**
 * Der Balkenplan der Kommunikation in klein: die Phasen untereinander, an jeder
 * beide Kanäle. Die Aussage ist die Vollständigkeit — es gibt keine Phase, in
 * der die Anwendung schweigt.
 */
export function TimelineCard({ timeline }) {
	return (
		<div className={styles.timeline}>
			<p className={styles.timelineLabel}>{timeline.label}</p>

			<ul className={styles.timelineList}>
				{timeline.phases.map(phase => (
					<li key={phase}>
						<span className={styles.timelineDot} />
						<span className={styles.timelinePhase}>{phase}</span>
						<span className={styles.timelineChannels}>
							<i className={styles.timelineMail} title="E-Mail" />
							<i className={styles.timelineInternal} title="Interne Mitteilung" />
						</span>
					</li>
				))}
			</ul>

			<p className={styles.timelineLegend}>
				<i className={styles.timelineMail} /> E-Mail
				<i className={styles.timelineInternal} /> intern
			</p>

			{timeline.note && <p className={styles.timelineNote}>{timeline.note}</p>}
		</div>
	)
}

TimelineCard.propTypes = { timeline: PropTypes.object.isRequired }

export default function Slide({ slide, taskDone, onTaskCheck, taskChecking }) {
	if (slide.kind === 'intro' || slide.kind === 'outro') {
		return (
			<div className={clsx(styles.slide, styles.slideCenter)}>
				<div className={styles.centerText}>
					{/* Der Name der Sache steht klein über dem Titel — der Titel selbst
					    soll begrüßen, nicht benennen. */}
					{slide.kicker && <p className={styles.kicker}>{slide.kicker}</p>}
					<h2 className={styles.bigTitle}>{slide.title}</h2>
					{slide.lead && <p className={styles.lead}>{slide.lead}</p>}
					{slide.bullets && (
						<ul className={styles.bullets}>
							{slide.bullets.map(text => (
								<li key={text}>{text}</li>
							))}
						</ul>
					)}
				</div>
				{slide.image && <img className={styles.centerImage} src={slide.image} alt="" />}
			</div>
		)
	}

	return (
		<div className={styles.slide}>
			<header className={styles.head}>
				<div>
					{slide.kicker && <p className={styles.kicker}>{slide.kicker}</p>}
					<h2 className={styles.title}>{slide.title}</h2>
				</div>
				<SlideMeta audience={slide.audience} priority={slide.priority} />
			</header>

			<p className={styles.goal}>{slide.goal}</p>

			<div className={styles.body}>
				<div className={styles.columns}>
					{slide.facts && (
						<section className={styles.col}>
							{/*
							 * „Was neu ist" und „Was das für euch heißt" — die beiden
							 * Fragen, die eine Verwaltung an eine Neuerung hat. Wo
							 * eine Folie andere braucht (die Wahlmöglichkeiten bei
							 * der Hygiene), stehen sie als `factsTitle`/`rulesTitle`
							 * am Inhalt.
							 */}
							<h3 className={styles.colTitle}>{slide.factsTitle || 'Was neu ist'}</h3>
							<ul className={styles.list}>
								{slide.facts.map(text => (
									<li key={text} dangerouslySetInnerHTML={{ __html: markup(text) }} />
								))}
							</ul>
						</section>
					)}

					{/*
					 * Entweder die Regeln oder die Checkliste — nie beides. Die
					 * Checkliste **ist** die Antwort auf „woran hängt es", nur
					 * gezeigt statt aufgezählt.
					 */}
					{slide.checklist ? (
						<section className={styles.col}>
							<h3 className={styles.colTitle}>{slide.rulesTitle || 'So sehen es alle drei Rollen'}</h3>
							<ChecklistCard checklist={slide.checklist} />
						</section>
					) : (
						slide.rules && (
							<section className={styles.col}>
								<h3 className={styles.colTitle}>{slide.rulesTitle || 'Was das für euch heißt'}</h3>
								<ul className={clsx(styles.list, styles.listRules)}>
									{slide.rules.map(text => (
										<li key={text} dangerouslySetInnerHTML={{ __html: markup(text) }} />
									))}
								</ul>
							</section>
						)
					)}
				</div>

				{/* Die Seitenspalte trägt eines von dreien: Bild, Verweis oder
				    Zeitachse. Seit die Einstufung im Kopf steht, ist sie frei. */}
				{(slide.image || slide.linkOut || slide.timeline) && (
					<aside className={styles.side}>
						{slide.image && <img className={styles.image} src={slide.image} alt="" />}
						{slide.linkOut && <LinkCard link={slide.linkOut} />}
						{slide.timeline && <TimelineCard timeline={slide.timeline} />}
					</aside>
				)}
			</div>

			{slide.watch && (
				<p className={styles.watch}>
					<span className={styles.watchLabel}>Aufpassen</span>
					{slide.watch}
				</p>
			)}

			{/*
			 * Die Aufgabe wird **hier** erledigt und nicht in einem zweiten Tab.
			 * Vorher stand hier ein Verweis ins Profil und ein Knopf „Nachsehen" —
			 * drei Schritte für eine Sache, die einer ist.
			 */}
			{slide.task && <AvatarTask aufgabe={slide.task} erledigt={taskDone} onErledigt={onTaskCheck} />}
		</div>
	)
}

/*
 * **Sternchen zu fett, sonst nichts.** Die Inhalte stehen als Fließtext in
 * `content.js`, und dort ist ein `<b>` mitten im Satz schlecht zu lesen. Alles
 * andere wird escaped: Der Text kommt zwar aus dem eigenen Repository, aber
 * `dangerouslySetInnerHTML` mit ungeprüftem Inhalt ist die Art von Abkürzung,
 * die genau einmal gutgeht.
 */
export const markup = text =>
	String(text)
		.replace(/&/g, '&amp;')
		.replace(/</g, '&lt;')
		.replace(/>/g, '&gt;')
		.replace(/\*\*(.+?)\*\*/g, '<b>$1</b>')

Slide.propTypes = {
	slide: PropTypes.object.isRequired,
	taskDone: PropTypes.bool,
	onTaskCheck: PropTypes.func,
	taskChecking: PropTypes.bool
}
